import React from 'react';
import { withRouter } from 'react-router';
import { Alert, Modal, Spinner, Table, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PageStatus } from 'enums';
import { Show } from 'Layout';
import { SurveysAPI, AuthAPI, MasterDataAPI, PartnersAPI } from "../../API";
import Select from 'react-select';
import { DashboardTemplates } from "./DashboardTemplates";
import { SurveyEmailSchedule } from "./SurveyEmailSchedule";
import moment from "moment";
import UsersPaginations from "./UsersPaginations";
import { exportToExcel, exportToCSV } from "../../Utils/ExportToExcel";
import { ManualSurveyRewards } from "./ManualSurveyRewards";
import PaginationNumbering from '../ShowEntries/Pagination';
import { baseApiURL, surveysURL } from "../../Utils/urls";
// axios instance directly so the panelist-only export can pass an
// `exportType` query string without widening the shared SurveysAPI surface
// (self-audit scope is limited to this file + the backend controller).
import { api } from "../../axiosConfig";
import { isPanelCountry, isPanelCountryByName } from './surveyConstants';
import { isIndiaMarket } from './indiaPollsDefaults';
import { getSurveyDisplayName } from '../../Utils/surveyDisplayName';
import csvtojson from "csvtojson";
import * as XLSX from "xlsx";
import { PartnerRspdtIds } from './PartnerRspdtIds';
import { InFieldStats, STATUS_OPTIONS as IN_FIELD_STATUS_OPTIONS } from './InFieldStats';
import { InFieldFilter, filterFileSuffix } from '../../Utils/istDateFilter';
import SurveyChangeLog from '../ChangeLog/SurveyChangeLog';
import { endLinkUrl } from '../../Utils/endLinks';
import { ReconciliationUpload } from './ReconciliationUpload';

/** In-Field Status value -> its dropdown label ('Open' -> 'Live'). */
const inFieldStatusLabel = (v: string): string =>
    (IN_FIELD_STATUS_OPTIONS.find((o) => o.value === v) || { label: v }).label;

const COUNTRY_PANEL_URL = process.env.REACT_APP_COUNTRY_PANEL_URL || 'https://polls.dataxing.com';
// Callback URLs point at the brand-neutral DX (admin) host where
// SurveyCallbackPage is served. REACT_APP_DX_BASE_URL is the production
// override (e.g. https://dataxing.com); falls back to the browser origin so
// local dev and existing deployments work without setting it.
const ADMIN_PANEL_URL = process.env.REACT_APP_DX_BASE_URL || window.location.origin;

const PANEL_COUNTRY_LABELS: Record<string, string> = {
    IN: 'Indiapolls',
    UK: 'UKpolls',
    MY: 'Malaysiapolls',
    PH: 'Philippinespolls',
    JP: 'Nipponpolls',
    SG: 'Singaporepolls',
};
const getDetailsLabel = (shortName?: string | null): string => {
    if (!shortName) return 'DataXing Details';
    return `${PANEL_COUNTRY_LABELS[shortName] || shortName} Details`;
};

type State = {
    survey: any,
    status: any,
    error: string | null,
    pageSize: any,
    currentPageNumber: any,
    loading: any,
};

const MODAL_TYPES = {
    NONE: 'NONE',
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    DETAILS: 'DETAILS',
    BODY: 'BODY'
};

const calculateTime = (createdAt, updatedAt) => {
    const created = new Date(createdAt);
    const updated = new Date(updatedAt);
    // @ts-ignore
    const timeDifferenceInMilliseconds = updated - created;
    return timeDifferenceInMilliseconds / (1000 * 60);
}

function calculateAge(dateOfBirth) {
    const birthDate = new Date(dateOfBirth);
    const currentDate = new Date();
    const age = currentDate.getFullYear() - birthDate.getFullYear();

    if (
        currentDate.getMonth() < birthDate.getMonth() ||
        (currentDate.getMonth() === birthDate.getMonth() &&
            currentDate.getDate() < birthDate.getDate())
    ) {
        return age - 1;
    }

    return age;
}

function extractDisplaySuffix(fullName: string | null | undefined, parentName: string, shortName: string): string {
    if (!fullName) return '';
    const trimmed = String(fullName).trim();
    if (!trimmed) return '';
    const base = `${parentName}_${shortName}`;
    const fullPrefix = `${base}_`;
    if (trimmed === base) return '';
    if (trimmed.startsWith(fullPrefix)) return trimmed.substring(fullPrefix.length);
    const orphanBase = `_${shortName}`;
    const orphanPrefix = `_${shortName}_`;
    if (trimmed === orphanBase) return '';
    if (trimmed.startsWith(orphanPrefix)) return trimmed.substring(orphanPrefix.length);
    
    // Fallback: If parent name changed, the string might be `OldParent_IN_Suffix`.
    const fallbackPrefix = `_${shortName}_`;
    const lastIndex = trimmed.lastIndexOf(fallbackPrefix);
    if (lastIndex !== -1) {
        return trimmed.substring(lastIndex + fallbackPrefix.length);
    }
    if (trimmed.endsWith(`_${shortName}`)) return '';
    
    return '';
}

class DashboardDetails extends React.Component<any, any> {
    // Hey! sonarqube, Please ignore this code
    // static defaultProps = {
    //     languageId: null,
    //     hideMenu: false,
    //     onUpdate: () => null,
    // };

    constructor(props) {
        super(props);
        this.state = {
            survey: null,
            status: PageStatus.None,
            error: null,
            users: [],
            samples: [],
            partnerUsers: [],
            showManual: false,
            selectedSample: '',
            partnerDetails: [],
            // Filter last applied in the In-Field Stats grid (dates + Status);
            // the partner xlsx exports use the same one so they list exactly
            // the grid's partner rows (DXD-7359). {} = all time, all statuses.
            partnerExportFilter: {},
            dxPanelDetails: [],
            pageSize: 100,
            currentPageNumber: 1,
            totalResponseData: '',
            totalResponsePages: '',
            loading: false,
            name: '',
            showCloneModal: false,
            cloneTypeValue: '',
            surveyIdValue: this.props.id,
            projectId: "",
            projectName: "",
            groupCloneVar: "N",
            showTestUniqueLinkModal: false,
            showReconcile: false,
            bulkLinks: [],
            showUploadLinkModal: false,
            bulkUploadLinkData: [],
            selectedPartner4TestLink: '',
            sendLinksTime: '',
            countries: [],
            selectedMoreCountryOption: [],
            newCountries: [],
            olderCountries: [],
            selectedRspdtStatus: "",
            selectedPrtnrId: "",
            isRespondentsExpanded: true,
            isTestUsersExpanded: true,
            isPanelistsExpanded: true,
            showPrtnrRspdtIds: false,
            // Partner × market expansion. Key: `${partnerId}|${market}`.
            expandedPartnerMarket: null as string | null,
            partnerMarketStatsCache: {} as { [partnerId: string]: any[] },
            loadingPartnerMarket: null as string | null,
        };
    }

    componentDidMount() {
        if (this.state.surveyIdValue) {
            this.fetchSurvey();
        }
    }

    // Universal returns B1: the end links below use the client's respondent-id
    // token (clients.id_token) when it has one. surveys.client is the name.
    fetchClientIdToken() {
        const name = this.state.survey?.client;
        if (!name) return;
        PartnersAPI.getClientByName(name)
            .then((client: any) => this.setState({ clientIdToken: (client && client.id_token) || null }))
            .catch(() => this.setState({ clientIdToken: null }));
    }

    fetchSurvey() {
        const { pageSize, currentPageNumber } = this.state;
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (!this.state.surveyIdValue) {
                    return Promise.reject(new Error('Invalid ID'));
                }
                return SurveysAPI.getOneDetails(this.state.surveyIdValue, pageSize, currentPageNumber);
            })
            .then((response: any) => {
                if (response.data) {
                    const survey: any = response.data;
                    this.setState({ survey: survey.data, samples: survey.samples, users: survey.users, totalResponsePages: response.totalPage, totalResponseData: response.totalCount, status: PageStatus.Loaded, groupCloneVar: survey.groupClone }, () => {
                        this.fetchPartners();
                        this.fetchClientIdToken();
                        // Skip the per-portal data fetch for non-panel markets — the
                        // table that consumes it is hidden there. (Render-side gate
                        // is in the JSX block below.)
                        if (isPanelCountry(this.state.survey?.shortName)) {
                            this.getCountryPortalDetails();
                        }
                    });
                }
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    getpantnerdetailsmain() {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (!this.state.surveyIdValue) {
                    return Promise.reject(new Error('Invalid ID'));
                }
                return SurveysAPI.getpantnerdetailsmain(this.state.surveyIdValue);
            })
            .then((partnerDetails: any) => {
                if (partnerDetails) { this.setState({ partnerDetails, status: PageStatus.Loaded }) }
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            }); 
    }

    getCountryPortalDetails() {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (!this.state.surveyIdValue) {
                    return Promise.reject(new Error('Invalid ID'));
                }
                return SurveysAPI.getOneDXPanelOnly(this.state.surveyIdValue);
            })
            .then((dxPanelDetails: any) => {
                if (dxPanelDetails) { this.setState({ dxPanelDetails, status: PageStatus.Loaded }) }
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    fetchPartners() {
        if (!this.state.survey?.surveypartners?.length) return;
        this.getpantnerdetailsmain();
        const partnerIds = this.state.survey.surveypartners.map((partner) => partner.partnerId);
        partnerIds.forEach((partnerId) => {
            this.fetchPartnerUsers(partnerId, this.state.surveyIdValue);
        });
    }


    fetchPartnerUsers(partnerId, surveyId) {
        if (partnerId && surveyId) {
            Promise.resolve()
                .then(() => SurveysAPI.partnerSurveyUsers(partnerId, surveyId))
                .then((response) => {
                    if (response.length > 0) {
                        const transformedData = response.map(item => ({
                            "Survey ID of the Project": this.state.surveyIdValue,
                            "Country": item.country,
                            "DataXing respondent ID": item.id,
                            "Vendor Respondent ID": item.rid,
                            "Status": item.status,
                            "Start IP": item.ip,
                            "End IP": item.ip,
                            "Start Time": item.createdAt,
                            "End Time": item.updatedAt,
                            "LOI (Minutes)": calculateTime(item.createdAt, item.updatedAt),
                            "Survey Name": item.surveyName,
                            "Partner Name": item.partnerName
                        }));
                        if (transformedData.length > 0) {
                            // @ts-ignore
                            this.setState(prev => ({ partnerUsers: prev.partnerUsers.concat(transformedData) }));
                        }
                    }
                })
                .catch((error) => {
                    console.log('ERROR: Error In exporting')
                });
        }
    };


    // Export. Routes through the shared axios `api` so the JWT Authorization
    // header is attached (a plain <a href> navigation cannot send that header
    // and the endpoint rejects it with "Token not verified"). The endpoint now
    // streams text/csv; received as a Blob and saved via a temporary
    // object-URL link (same pattern as Samples > Download Sample).
    //
    // Two callers share this handler — invariant: same endpoint, same auth,
    // only the exportType query param + filename suffix differ:
    //   • Export All Users (Details header)           → handleExport()
    //   • Per-portal "Export User" panelist icon      → handleExport('panelist')
    // The panelist branch filters the UNION to Source='panelist' rows server-
    // side (see getSurveyDetailsExport).
    async handleExport(exportType?: 'panelist') {
        try {
            const qs = exportType ? `?exportType=${encodeURIComponent(exportType)}` : '';
            const resp = await api.get(`${surveysURL}/getSurveyDetailsExport/${this.state.surveyIdValue}${qs}`, {
                responseType: 'blob',
            });
            const blob: Blob = resp.data as Blob;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const suffix = exportType === 'panelist' ? '-panelists' : '-users';
            link.download = `survey-${this.state.surveyIdValue}${suffix}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log('ERROR: Error In exporting', error);
        }
    };


    handlePartnerExport() {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (!this.state.surveyIdValue) {
                    return Promise.reject(new Error('Invalid ID'));
                }
                return SurveysAPI.getAllPartnerUsersexport(this.state.surveyIdValue, this.state.partnerExportFilter);
            })
            .then((exportData: any) => {
                if (exportData.length > 0) {
                    this.setState({ status: PageStatus.Loaded });
                    exportToExcel(exportData, `${this.props.unique_id} & Partner`);
                } else {
                    alert("No Data Present To Export.");
                    this.setState({ status: PageStatus.Loaded });
                }
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    };

    downloadManualRewards() {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (!this.state.surveyIdValue) {
                    return Promise.reject(new Error('Invalid ID'));
                }
                return AuthAPI.downloadManualRewards(this.state.surveyIdValue);
            })
            .then((exportData: any) => {
                if (Array.isArray(exportData) && exportData.length > 0) {
                    this.setState({ status: PageStatus.Loaded });
                    exportToExcel(exportData, `Rewards of survey (${this.state.survey?.uniqueid ? this.state.survey?.uniqueid : "Demo"})`);
                } else {
                    alert("Rewards Not Allotted.");
                    this.setState({ status: PageStatus.Loaded });
                }
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    };

    openModalFor(type) {
        this.setState({ showCloneModal: true, name: '', cloneTypeValue: type }, () => this.fetchOlderCountries());
    }

    cloneThisSurvey() {
        // Group Clone no longer collects a SMS/WhatsApp Name — fall back to projectName so
        // the cloned parent still has a non-empty `name` column. PMs edit per-country after.
        const isGroup = this.state.cloneTypeValue === "Group";
        const effectiveName = isGroup ? (this.state.name || this.state.projectName) : this.state.name;
        const nameOk = isGroup ? !!this.state.projectName : !!this.state.name;
        if (nameOk && this.state.projectName && this.state.projectId && this.state.newCountries.length > 0) {
            let payload = { projectName: this.state.projectName, projectId: this.state.projectId, id: this.state.surveyIdValue, name: effectiveName, newCountries: this.state.newCountries, cloneType: isGroup ? "group" : "individual" };
            Promise.resolve()
                .then(() => this.setState({ status: PageStatus.Loading }))
                .then(() => { return SurveysAPI.duplicatesurvey(payload); })
                .then((respoData: any) => { this.setState({ status: PageStatus.Loaded, name: '', showCloneModal: false, cloneTypeValue: '', surveyIdValue: respoData.Survey.id, newCountries: [], selectedMoreCountryOption: [], olderCountries: [], }, () => { this.fetchSurvey(); }); this.props.onUpdate("clone", respoData.Survey.id); alert("Survey cloned successfully.") })
                .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }); });
        } else {
            alert("All fields are required.");
        }
    }

    // Same-market segment copy for a CHILD survey. Clicks the new "Duplicate" button
    // on a child's Details modal — creates a sibling under the same parent with the
    // same country/shortName/uniqueid and a segment-numbered name. No form prompts.
    handleDuplicateChild = async () => {
        const survey = this.state.survey;
        if (!survey || !survey.id) return;
        const marketLabel = survey.country || survey.shortName || 'this market';
        const result = await Swal.fire({
            title: 'Duplicate this market setup?',
            text: `This will create a new segment for ${marketLabel} under the same parent project.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Duplicate',
        });
        if (!result.isConfirmed) return;
        try {
            this.setState({ status: PageStatus.Loading });
            const respData = await SurveysAPI.duplicateChild(survey.id);
            this.setState({ status: PageStatus.Loaded });
            const newId = respData && respData.Survey && respData.Survey.id;
            if (newId) {
                this.props.onUpdate && this.props.onUpdate("duplicate", newId);
            }
            Swal.fire({ icon: 'success', title: 'Done', text: 'Duplicate created successfully.' });
            this.fetchSurvey();
        } catch (err: any) {
            this.setState({ status: PageStatus.Error, error: err && err.message });
            const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to duplicate.';
            Swal.fire({ icon: 'error', title: 'Error', text: msg });
        }
    }

    clone4sameMarketSurvey() {
        if (this.state.name && this.state.projectName && this.state.projectId && this.state.newCountries.length > 0) {
            let payload = { projectName: this.state.projectName, projectId: this.state.projectId, id: this.state.surveyIdValue, name: this.state.name, newCountries: this.state.newCountries, cloneType: this.state.cloneTypeValue == "Group" ? "group" : "individual" };
            Promise.resolve()
                .then(() => this.setState({ status: PageStatus.Loading }))
                .then(() => { return SurveysAPI.duplicatesurveyWithSameRedirects(payload); })
                .then((respoData: any) => { this.setState({ status: PageStatus.Loaded, name: '', showCloneModal: false, cloneTypeValue: '', surveyIdValue: respoData.Survey.id, newCountries: [], selectedMoreCountryOption: [], olderCountries: [], }, () => { this.fetchSurvey(); }); this.props.onUpdate("clone", respoData.Survey.id); alert("Survey cloned successfully for same market.") })
                .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }); });
        } else {
            alert("All fields are required.");
        }
    }

    detectClientDevice() {
        const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
        if (/ipad|tablet|kindle|silk|playbook/.test(ua)) return 'tablet';
        if (/android/.test(ua) && !/mobile/.test(ua)) return 'tablet';
        if (/iphone|ipod|windows phone|blackberry|opera mini|mobile/.test(ua)) return 'mobile';
        return 'desktop';
    }

    async testCurrentSurvey() {
        if (!this.state.survey?.parentid) {
            Swal.fire({ icon: 'info', title: 'Select a Child Market', text: 'Test URLs are configured per country. Please expand the project and select a specific country to test.' });
            return;
        }
        
        const useUniqueLinks = !!this.state.survey?.useUniqueLinks;
        const testUrl = this.state.survey?.testurl;
        if (!useUniqueLinks && !testUrl) {
            Swal.fire({ icon: 'warning', title: 'No Test URL', text: 'Test URL has not been configured for this survey yet. Please set the Survey Test URL in the survey form.' });
            return;
        }

        // Device-compatibility client-side pre-check. The server-side gate
        // in createSurveyPartnerUser is still the source of truth for real
        // panelist hits — this just prevents PMs from wasting a click testing
        // on a device the survey isn't configured for.
        const compat = this.state.survey?.deviceCompatibility;
        if (compat) {
            const allowed = String(compat).split(',').map((d: string) => d.trim().toLowerCase()).filter(Boolean);
            const current = this.detectClientDevice();
            if (allowed.length > 0 && !allowed.includes(current)) {
                const pretty = (d: string) => ({ mobile: 'Mobile', desktop: 'Desktop/Laptop', tablet: 'Tablet' } as any)[d] || d;
                Swal.fire({
                    icon: 'warning',
                    title: 'Device Not Supported',
                    html: `This survey is only available on <strong>${allowed.map(pretty).join(', ')}</strong>.<br/>You are currently on <strong>${pretty(current)}</strong>.`,
                });
                return;
            }
        }

        // Persist a TestSurveyUsers row server-side so the test session has a real DB-backed ID
        // that can be cross-verified later. The backend generates the UUID; we use that ID for both
        // the alert and the URL so the popup ID matches the row in the DB and the hit at the client.
        // If the call fails we fall back to a local random ID — the test still works, just isn't tracked.
        let testUserId: string | null = null;
        let resolvedUrl: string | null = null;
        try {
            const result = await SurveysAPI.testSurvey({ surveyId: this.state.survey.id });
            // Backend returns { testUrl, testUserId, sessionUrl? }. sessionUrl
            // (universal returns A2) is our /r/s/<id> hop: it sets the
            // respondent cookie, then sends the browser on to testUrl. The
            // popup ID is testUserId; older backends only send testUrl, so the
            // ID is then read from that URL.
            const backendTestUrl: string | undefined = result?.testUrl;
            if (backendTestUrl) {
                resolvedUrl = result?.sessionUrl || backendTestUrl;
                if (result?.testUserId) {
                    testUserId = String(result.testUserId);
                } else {
                    // Extract the UUID the backend just inserted. Match standard UUID v4 anywhere in the URL.
                    const uuidMatch = backendTestUrl.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
                    if (uuidMatch) {
                        testUserId = uuidMatch[0];
                    }
                }
            }
        } catch (err: any) {
            console.warn('[testCurrentSurvey] Failed to persist test user:', err);
            if (useUniqueLinks) {
                const msg = err?.response?.data?.message || err?.message || 'Test unique links exhausted.';
                Swal.fire({ icon: 'error', title: 'Test Failed', text: msg });
                return;
            }
        }

        if (!testUserId || !resolvedUrl) {
            if (useUniqueLinks) {
                Swal.fire({ icon: 'error', title: 'Test Failed', text: 'Backend returned an empty test unique link.' });
                return;
            }
            // Fallback: locally-generated ID, locally-substituted URL. Not tracked in DB.
            testUserId = 'TEST_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now();
            // Mirrors backend injectUserId behavior — keep in sync until a second
            // frontend caller justifies a shared helper.
            // Strict: if the test URL has no placeholder, surface the misconfig
            // to the PM via alert rather than silently opening a placeholder-less URL.
            const placeholderRegex = /\[USER_ID\]|\{userid\}|\{user_id\}/gi;
            if (!placeholderRegex.test(testUrl!)) {
                alert('Test URL has no [USER_ID] / {userid} / {user_id} placeholder. Update the survey configuration before testing.');
                return;
            }
            const encoded = encodeURIComponent(testUserId);
            resolvedUrl = testUrl!.replace(/\[USER_ID\]|\{userid\}|\{user_id\}/gi, encoded);
        }

        // Show the ID to the PM so they can cross-verify with client
        alert('Test session started with user ID:\n' + testUserId + '\n\nUse this ID to confirm the client recorded the same hit.');

        window.open(resolvedUrl, '_blank');
    }

    async copyTestCurrentSurveyLink() {
        if (!this.state.survey?.parentid) {
            Swal.fire({ icon: 'info', title: 'Select a Child Market', text: 'Test URLs are configured per country. Please expand the project and select a specific country to copy the link for.' });
            return;
        }

        const useUniqueLinks = !!this.state.survey?.useUniqueLinks;
        const testUrl = this.state.survey?.testurl;
        if (!useUniqueLinks && !testUrl) {
            Swal.fire({ icon: 'warning', title: 'No Test URL', text: 'No test URL configured for this market. Please set the Survey Test URL in the survey form.' });
            return;
        }

        // Generate the test URL with the same logic as the Test button
        let testUserId: string | null = null;
        let resolvedUrl: string | null = null;
        try {
            const result = await SurveysAPI.testSurvey({ surveyId: this.state.survey.id });
            const backendTestUrl: string | undefined = result?.testUrl;
            if (backendTestUrl) {
                resolvedUrl = backendTestUrl;
                const uuidMatch = backendTestUrl.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
                if (uuidMatch) {
                    testUserId = uuidMatch[0];
                }
            }
        } catch (err: any) {
            console.warn('[copyTestCurrentSurveyLink] Failed to persist test user:', err);
            if (useUniqueLinks) {
                const msg = err?.response?.data?.message || err?.message || 'Test unique links exhausted.';
                Swal.fire({ icon: 'error', title: 'Test Failed', text: msg });
                return;
            }
        }

        if (!testUserId || !resolvedUrl) {
            if (useUniqueLinks) {
                Swal.fire({ icon: 'error', title: 'Test Failed', text: 'Backend returned an empty test unique link.' });
                return;
            }
            testUserId = 'TEST_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now();
            const placeholderRegex = /\[USER_ID\]|\{userid\}|\{user_id\}/gi;
            if (!placeholderRegex.test(testUrl!)) {
                alert('Test URL has no [USER_ID] / {userid} / {user_id} placeholder. Update the survey configuration before copying.');
                return;
            }
            const encoded = encodeURIComponent(testUserId);
            resolvedUrl = testUrl!.replace(/\[USER_ID\]|\{userid\}|\{user_id\}/gi, encoded);
        }

        navigator.clipboard.writeText(resolvedUrl);
        Swal.fire({ icon: 'success', title: 'Copied!', text: `Test URL copied to clipboard with User ID:\n${testUserId}` });
    }

    showTestUniqueLinksModal() {
        this.setState({ showTestUniqueLinkModal: true })
    }

    downloadSampleLinks(type) {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => {
                if (this.state.sendLinksTime == "secondTimeSend") {
                    return SurveysAPI.resendpartnerUsersDownload({ id: this.state.surveyIdValue, partnerId: this.state.selectedPartner4TestLink });
                }
                let obj;
                let fileName;
                if (type === 'sample') {
                    obj = [{ url: "" }];
                    fileName = 'sample of test unique links';
                } else if (this.state.sendLinksTime === "firstTimeSend") {
                    obj = [{ link: '', dxUserId: '' }];
                    fileName = "upload partner test link sample";
                } else {
                    obj = [{ link: '', userId: '', dxUserId: '' }];
                    fileName = "resend partner test link sample";
                }

                exportToCSV(obj, fileName);
                return [];
            })
            .then((respo) => {
                if (respo.length > 0) {
                    for (const item of respo) { item.link = ""; item.dxUserId = ""; }
                    exportToCSV(respo, `${this.state.selectedPartner4TestLink} & resend partner users download`);
                };
                this.setState({ status: PageStatus.Loaded });
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    submitTestUniqueLinksFile() {
        if (this.state.bulkLinks.length < 1) {
            alert("Check file extention, only csv file is accepted.");
            return;
        }
        let payload = {
            surveyId: this.state.surveyIdValue,
            bulkLinks: this.state.bulkLinks,
        };
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => { return SurveysAPI.testuniqueLinks(payload); })
            .then((respoData: any) => {
                alert("Test unique links uploaded successfully.");
                const ADMIN_PANEL_URL = process.env.REACT_APP_DX_BASE_URL || window.location.origin;
                const correctedData = respoData.map((item: any) => {
                    if (item.temporarySurveyLink) {
                        item.temporarySurveyLink = item.temporarySurveyLink.replace(
                            'https://dataxing.com:9000', 
                            ADMIN_PANEL_URL
                        );
                    }
                    return item;
                });
                exportToExcel(correctedData, `Test_Unique_Links_${this.state.surveyIdValue}`);
                this.setState({ status: PageStatus.Loaded, showTestUniqueLinkModal: false }, () => { this.fetchSurvey(); })
            })
            .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }); });
    }

    parseTestUniqueLinksFile = (file: File): Promise<{ urls: string[]; errors: string[] }> => {
        return new Promise((resolve) => {
            const isExcel = file.name.match(/\.xlsx?$/i);
            const reader = new FileReader();

            const processCandidates = (candidates: string[]) => {
                candidates = candidates
                    .map(c => c.replace(/[,;\s]+$/, ''))
                    .filter(c => c.length > 0);
                const valid: string[] = [];
                const errors: string[] = [];
                candidates.forEach((u, i) => {
                    if (!/^https?:\/\//i.test(u)) {
                        errors.push(`Row ${i + 1}: not a valid URL (${u.slice(0, 60)})`);
                    } else {
                        valid.push(u);
                    }
                });
                resolve({ urls: valid, errors });
            };

            if (isExcel) {
                reader.onload = () => {
                    try {
                        const data = reader.result;
                        const workbook = XLSX.read(data, { type: 'binary' });
                        const sheetName = workbook.SheetNames[0];
                        const sheet = workbook.Sheets[sheetName];
                        const parsed: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                        let candidates: string[] = [];
                        if (parsed.length > 0) {
                            const firstRow = parsed[0];
                            const isUrlHeader = String(firstRow[0] || '').toLowerCase() === 'url' || String(firstRow[0] || '').toLowerCase() === 'link';
                            if (isUrlHeader) {
                                candidates = parsed.slice(1).map(r => String(r[0] || '').trim());
                            } else {
                                candidates = parsed.map(r => String(r[0] || '').trim());
                            }
                        }
                        processCandidates(candidates);
                    } catch (e: any) {
                        resolve({ urls: [], errors: [`Parse error: ${e?.message || 'unknown'}`] });
                    }
                };
                reader.onerror = () => resolve({ urls: [], errors: [reader.error?.message || 'Read failed.'] });
                reader.readAsBinaryString(file);
            } else {
                reader.onload = async () => {
                    try {
                        const content = (reader.result as string) || '';
                        if (!content.trim()) { resolve({ urls: [], errors: ['File is empty.'] }); return; }
                        let candidates: string[] = [];
                        const parsed = await csvtojson({ trim: true, flatKeys: true }).fromString(content);
                        if (parsed.length > 0) {
                            const keys = Object.keys(parsed[0]);
                            const urlKey = keys.find(k => k.toLowerCase() === 'url' || k.toLowerCase() === 'link');
                            if (urlKey) {
                                candidates = parsed.map((r: any) => String(r[urlKey] || '').trim());
                            } else if (keys.length === 1) {
                                // Single-column with arbitrary header — treat header as a row too.
                                const onlyKey = keys[0];
                                candidates = [onlyKey, ...parsed.map((r: any) => String(r[onlyKey] || '').trim())];
                            }
                        }
                        // Fallback: line-by-line if csvtojson produced nothing useful.
                        if (candidates.length === 0) {
                            candidates = content.split(/\r?\n/).map(l => l.trim());
                        }
                        processCandidates(candidates);
                    } catch (e: any) {
                        resolve({ urls: [], errors: [`Parse error: ${e?.message || 'unknown'}`] });
                    }
                };
                reader.onerror = () => resolve({ urls: [], errors: [reader.error?.message || 'Read failed.'] });
                reader.readAsText(file);
            }
        });
    };

    handleTestUniqueLinksFile = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.name.match(/\.(csv|xlsx?)$/i)) {
                alert("Only CSV and Excel files are accepted. Please check the file extension.");
                return;
            }
            try {
                const { urls, errors } = await this.parseTestUniqueLinksFile(file);
                if (urls.length < 1) {
                    alert("No valid links found. Please ensure the file has a column named 'link' or 'url' or contains valid URLs.");
                    return;
                }
                const bulkLinks = urls.map(url => ({ link: url }));
                this.setState({ bulkLinks });
            } catch (error) {
                console.error('Error parsing file:', error);
            }
        }
    };

    convertCsvToJson = (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const csvContent: any = reader.result;

                if (!csvContent.trim()) {
                    reject(new Error('CSV content is empty.'));
                    return;
                }

                csvtojson()
                    .fromString(csvContent)
                    .then(jsonData => resolve(jsonData));
            };

            reader.onerror = (event) => {
                reject(new Error('FileReader encountered an error'));
            };

            reader.readAsText(file);
        });
    };

    getCaseInsensitiveKey = (obj: any, targetKey: string) => {
        if (!obj) return undefined;
        return Object.keys(obj).find(k => k.trim().replace(/^\uFEFF/, '').toLowerCase() === targetKey.toLowerCase());
    };

    modifiedConvertedJson = (obj) => {
        const linkKey = this.getCaseInsensitiveKey(obj, 'link') || this.getCaseInsensitiveKey(obj, 'url');
        if (linkKey && obj[linkKey]) {
            return { link: obj[linkKey] };
        }
        return null;
    }

    uploadFileModal = (id, type) => {
        this.setState({ showUploadLinkModal: true, selectedPartner4TestLink: id, sendLinksTime: type, });
    }

    downloadFileFunc = (partnerId): Promise<void> => {
        let payload = { surveyId: this.state.surveyIdValue, partnerId };
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => { return SurveysAPI.downloadPartnerUniqueLinks(payload) })
            .then((respo) => { this.setState({ status: PageStatus.Loaded }, () => exportToExcel(respo, `${partnerId} & Partner Links`)) })
            .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }) })

    }

    downloadPartnerUsersFunc = (partnerId): Promise<void> => {
        // Same filter as the In-Field Stats grid above (plain dates cut at India
        // midnight + Status); the backend applies the grid's row rules, so the
        // sheet has exactly this partner's share of the grid's rows.
        const filter: InFieldFilter = this.state.partnerExportFilter || {};
        let payload = { id: this.state.surveyIdValue, partnerId, ...filter };
        return Promise.resolve()
            .then(() => { this.setState({ status: PageStatus.Loading }) })
            .then(() => { return SurveysAPI.getPartnerUsersexport(payload) })
            .then((respo) => {
                this.setState({ status: PageStatus.Loaded });
                // Empty result still produces a usable headers-only sheet rather
                // than blocking the PM with an alert. PM gets a download every
                // time, even for partners with zero hits so far.
                const rows = Array.isArray(respo) ? respo : [];
                const partner = (this.state.partnerDetails || []).find((p: any) => p.partnerId === partnerId);
                const partnerName = (partner?.partner_name || partnerId).toString().replace(/[^a-zA-Z0-9._-]+/g, '_');
                const projectId = (this.state.survey?.projectId || this.state.surveyIdValue || 'survey').toString().replace(/[^a-zA-Z0-9._-]+/g, '_');
                const filename = `${projectId}_${partnerName}_export${filterFileSuffix(filter, inFieldStatusLabel)}`;
                if (rows.length === 0) {
                    // exportToExcel on an empty array writes a sheet with no header
                    // row — give it a single placeholder row built from the
                    // backend's column keys so the headers materialize.
                    const headerOnly = [{
                        'Project': '', 'Country': '', 'Partner Name': '', 'User Id': '', 'DX User Id': '',
                        'Name': '', 'Gender': '', 'Partner RID': '', 'Status': '', 'Survey Type': '',
                        'Start IP': '', 'End IP': '', 'Device Type': '',
                        'Started At': '', 'Completed At': '', 'LOI (Minutes)': '', 'Substituted Survey Link': '',
                    }];
                    exportToExcel(headerOnly, filename);
                } else {
                    exportToExcel(rows, filename);
                }
            })
            .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }) })
    }

    handleUploadfile = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                alert("Only CSV files are accepted. Please check the file extension.");
                return;
            }
            try {
                const jsonData = await this.convertCsvToJson(file);
                const transformedData = jsonData.map(this.modifiedFileConvertedJson);
                const filteredData = transformedData.filter(item => item !== null && item !== undefined);
                if (filteredData.length < 1) {
                    alert("No valid data found. Please ensure the CSV has the required columns.");
                    return;
                }
                this.setState({ bulkUploadLinkData: filteredData });
            } catch (error) {
                console.error('Error converting CSV to JSON:', error);
            }
        }
    };

    modifiedFileConvertedJson = (obj) => { 
        const linkKey = this.getCaseInsensitiveKey(obj, 'link');
        const dxUserIdKey = this.getCaseInsensitiveKey(obj, 'dxUserId');
        const userIdKey = this.getCaseInsensitiveKey(obj, 'userId');

        if (!linkKey || !obj[linkKey]) return null;

        return this.state.sendLinksTime == "firstTimeSend" 
            ? { link: obj[linkKey], dxUserId: dxUserIdKey ? obj[dxUserIdKey] : undefined } 
            : { link: obj[linkKey], userId: userIdKey ? obj[userIdKey] : undefined, dxUserId: dxUserIdKey ? obj[dxUserIdKey] : undefined };
    }

    uplaodPartnerLinkFunc = (): Promise<void> => {
        let payload = { partnerId: this.state.selectedPartner4TestLink, surveyId: this.state.surveyIdValue, partnerLinks: this.state.bulkUploadLinkData, };
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => { return this.state.sendLinksTime == "firstTimeSend" ? SurveysAPI.testuniqueLinksPartners(payload) : SurveysAPI.resendpartnerUserLinks(payload) })
            .then((respo) => { this.setState({ status: PageStatus.Loaded, sendLinksTime: '', bulkUploadLinkData: [], selectedPartner4TestLink: '', showUploadLinkModal: false, }); alert("Links uploaded successfully.") })
            .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }) })
    }

    fetchOlderCountries = (): Promise<void> => {
        let payload = { id: this.state.surveyIdValue, cloneType: this.state.cloneTypeValue };
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => SurveysAPI.getMarketsOfSurvey(payload))
            .then((respo) => { this.setState({ status: PageStatus.Loaded, olderCountries: respo }, () => this.fetchCountryList()); })
            .catch((error) => { this.setState({ status: PageStatus.Error, error: error.message }) })
    }

    fetchCountryList(): Promise<void> {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => MasterDataAPI.countryList('10'))
            .then((countries) => {
                const options = countries.map(country => ({
                    label: country.name,
                    value: country.name,
                    shortName: country.shortName,
                }));
                options.sort((a, b) => {
                    if (a.label < b.label) { return -1; }
                    if (a.label > b.label) { return 1; }
                    return 0;
                });
                this.setState({ countries: options, status: PageStatus.Loaded });
                if (this.state.olderCountries.length > 0) {
                    const option = options.filter(item => this.state.olderCountries.some((name) => item.label === name.country));
                    let countryName = option.map((ele) => { return { name: ele.label, shortName: ele.shortName } });
                    this.setState({ selectedMoreCountryOption: option, newCountries: countryName, });
                }
            })
            .catch((error) => {
                this.setState({ error: error.message, status: PageStatus.Error });
            });
    }

    handleMoreCountryChange = async (selectedMoreCountryOption) => {
        let countryName = selectedMoreCountryOption.map((ele) => { return { name: ele.label, shortName: ele.shortName } });
        this.setState({ newCountries: countryName, selectedMoreCountryOption });
    };

    handlePageNumberChange = (newValue: any) => { this.setState({ currentPageNumber: newValue }, () => { this.fetchSurvey() }); };
    showPartnerRspdtIds = (pId, rspdtaStatus) => {
        this.setState((prevStates) => ({
            ...prevStates,
            selectedPrtnrId: pId,
            selectedRspdtStatus: rspdtaStatus,
        }), () => {
            this.setState((prevStates) => ({
                ...prevStates,
                showPrtnrRspdtIds: true
            }))
        })
    };

    /**
     * Toggle the per-market stats row for a (partner, market) pair.
     * Fetches once per partner and caches the full market breakdown; subsequent
     * clicks for other markets of the same partner are served from cache.
     */
    togglePartnerMarket = async (partnerId: string, market: string) => {
        const key = `${partnerId}|${market}`;
        if (this.state.expandedPartnerMarket === key) {
            this.setState({ expandedPartnerMarket: null });
            return;
        }
        if (this.state.partnerMarketStatsCache[partnerId]) {
            this.setState({ expandedPartnerMarket: key });
            return;
        }
        try {
            this.setState({ loadingPartnerMarket: key });
            const stats = await SurveysAPI.getPartnerMarketStats(this.state.surveyIdValue, partnerId);
            this.setState((prev: any) => ({
                partnerMarketStatsCache: { ...prev.partnerMarketStatsCache, [partnerId]: stats },
                expandedPartnerMarket: key,
                loadingPartnerMarket: null,
            }));
        } catch (err) {
            console.error('Failed to load partner market stats:', err);
            this.setState({ loadingPartnerMarket: null });
        }
    };

    render() {
        const { pageSize, currentPageNumber, totalResponseData, totalResponsePages } = this.state;
        const showSurveyStatus = (val) => {
            let obj = {
                "Open": "Live",
                "Hold": "On Hold",
                "Wait": "Ids Awaited",
                "Close": "End",
                "Test": "Test",
            };
            return obj[val] ?? "";
        }
        const sendSurveyByValue = (): string => {
            let exArray: string[] = [];
            if (this.state.survey?.sms === "Y") exArray.push("SMS");
            if (this.state.survey?.whatsapp === "Y") exArray.push("Whats-App");
            if (this.state.survey?.email === "Y") exArray.push("Email");
            return exArray.join(", ");
        }
        const setYNinRespo = (cond) => { if (cond) { return "Yes" } return "No"; }
        const dropoutCalculation = (totalDropout, totalHits) => {
            let calculatedVal = 0;
            if (totalHits == 0) { return calculatedVal; }
            if (!isNaN(Number(totalDropout)) && !isNaN(Number(totalHits)))
                calculatedVal = ((Number(totalDropout) / Number(totalHits)) * 100).toFixed(2);
            return calculatedVal;
        }
        const setStyleVal = (detail, status, val) => {
            return <button
                className='bg-transparent'
                onClick={() => this.showPartnerRspdtIds(detail.partnerId, status)}
                onMouseOver={(e) => (e.currentTarget.style.color = 'blue')}
                onFocus={(e) => (e.currentTarget.style.color = 'blue')}
                onMouseOut={(e) => (e.currentTarget.style.color = 'black')}
                onBlur={(e) => (e.currentTarget.style.color = 'black')}
            >
                {detail[val] ?? "-"}
            </button>;
        }

        return (
            <>
                {this.state.showPrtnrRspdtIds &&
                    <PartnerRspdtIds
                        partnerId={this.state.selectedPrtnrId}
                        surveyId={this.state.surveyIdValue}
                        surveyStatus={this.state.selectedRspdtStatus}
                        onClose={() => this.setState({
                            showPrtnrRspdtIds: false,
                            selectedRspdtStatus: "",
                        })}
                    />}
                <Modal
                    centered
                    size="xl"
                    backdrop="static"
                    onHide={this.props.onClose}
                    show
                    style={{ zIndex: 1201 }}
                >
                    <Modal.Header closeButton>
                        <h5 className="mb-0 mt-1">
                            Details
                            {this.state.survey?.parentid && (this.state.survey?.country || this.state.survey?.shortName) && (
                                <span
                                    className="badge badge-info ml-2"
                                    style={{ fontSize: '14px', verticalAlign: 'middle' }}
                                >
                                    {this.state.survey.country || this.state.survey.shortName}
                                </span>
                            )}
                        </h5>
                        <div
                            className="d-flex justify-content-end w-100 mr-2"
                        >
                            {!!this.state.survey?.parentid &&
                                <>
                                    <button
                                        type="button"
                                        className="btn-sm btn-info mr-2"
                                        onClick={() => this.copyTestCurrentSurveyLink()}
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'copy']}
                                            className="mr-2"
                                        />
                                        Copy Test Link
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-sm btn-info mr-2"
                                        onClick={() => this.testCurrentSurvey()}
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'list']}
                                            className="mr-2"
                                        />
                                        Test
                                    </button>
                                </>
                            }
                            {this.state.survey && !this.state.survey?.parentid &&
                                <span className="text-muted small mr-2" style={{ alignSelf: 'center' }}>
                                    Expand a child market to test
                                </span>
                            }
                            {/* Universal returns C1: record outcomes from the client's file. */}
                            {this.state.survey &&
                                <button
                                    type="button"
                                    className="btn-sm btn-info mr-2"
                                    onClick={() => this.setState({ showReconcile: true })}
                                >
                                    <FontAwesomeIcon
                                        icon={['fas', 'upload']}
                                        className="mr-2"
                                    />
                                    Reconcile
                                </button>
                            }

                            {/* Child surveys show "Duplicate" (same-market segment copy).
                                Parents show ONLY "Group Clone" (rendered below) — the
                                individual "Clone" button was removed so PMs can't create
                                an orphan single-country copy of a multi-country project. */}
                            {this.state.survey && this.state.survey.parentid && (
                                <button
                                    type="button"
                                    className="btn-sm btn-info mr-2"
                                    onClick={() => this.handleDuplicateChild()}
                                >
                                    <FontAwesomeIcon
                                        icon={['fas', 'clone']}
                                        className="mr-2"
                                    />
                                    Duplicate
                                </button>
                            )}

                            {this.state.survey && !this.state.survey.parentid && this.state.groupCloneVar == "Y" &&
                                <button
                                    type="button"
                                    className="btn-sm btn-info mr-2"
                                    onClick={() => this.openModalFor("Group")}
                                >
                                    <FontAwesomeIcon
                                        icon={['fas', 'clone']}
                                        className="mr-2"
                                    />
                                    Group Clone
                                </button>
                            }

                            {this.state.survey?.pointAllocationType !== 'Auto' && this.state.survey?.surveyType === 'Close' &&
                                <>
                                    <button
                                        type="button"
                                        onClick={() => this.setState((prev) => ({ showManual: !prev.showManual }))}
                                        className="btn-sm btn-primary mr-2"
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'upload']}
                                            className="mr-2"
                                        />
                                        Upload Incentive
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => this.downloadManualRewards()}
                                        className="btn-sm btn-success mr-2"
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'download']}
                                            className="mr-2"
                                        />
                                        Download Incentive
                                    </button>
                                </>
                            }

                            {/* Export All Users — wraps the same getSurveyDetailsExport
                                endpoint as the Indiapolls Details > Export User button.
                                That endpoint UNION ALLs assignedSurveys (test +
                                panelist) with partnerusers (partner), producing a
                                single 18-column workbook covering all three sources.
                                Per-row Partner Export buttons inside the Partners
                                table are unchanged and remain per-partner scoped. */}
                            {this.state.survey &&
                                <button type="button" className="btn btn-info ml-1" onClick={() => this.handleExport()}>Export All Users</button>
                            }


                        </div>
                    </Modal.Header>
                    <Modal.Body>

                        <Show when={this.state.status === PageStatus.Loading}>
                            <div className="d-flex justify-content-center w-100 p-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Show>

                        <Show when={this.state.status === PageStatus.Loaded && !!this.state.survey}>
                            {(() => {
                            const _isParent = !this.state.survey?.parentid;
                            const _showPanelFields = !_isParent && isPanelCountryByName(this.state.survey?.country);
                            // Callback URLs: both sets on every project, as server-side end links
                            // (SCRUM-130): GET /r/<status> on the admin backend (nginx proxies /r)
                            // records the outcome and 302s onward, with no "#/" and no JavaScript
                            // needed on the respondent's side. Dynamic links carry the surveyid;
                            // static links carry only {userid} and record against the
                            // respondent's latest open entry. Old "#/" and hash-free links still work.
                            // Universal returns B1: uid carries the client's own token when
                            // the client has one (clients.id_token), else {userid}.
                            const _dynamicUrl = (slug: string) => endLinkUrl(ADMIN_PANEL_URL, slug, this.state.clientIdToken, this.state.survey?.uniqueid);
                            const _staticUrl = (slug: string) => endLinkUrl(ADMIN_PANEL_URL, slug, this.state.clientIdToken);
                            const _callbackRows = (urlFor: (slug: string) => string) => ([
                                ['Complete URL', 'completed'],
                                ['Over Quota URL', 'overquota'],
                                ['Terminate URL', 'terminate'],
                                ['Quality Terminate URL', 'qualityterminate'],
                            ] as const).map(([label, slug]) => (
                                <div className="row mt-2" key={slug}>
                                    <div className="col">
                                        <strong>{label}: </strong>
                                        <a href={urlFor(slug)} target="_blank" rel="noopener noreferrer">
                                            {urlFor(slug)}
                                        </a>
                                    </div>
                                </div>
                            ));
                            return (<>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Survey Id: </strong>
                                    {this.state.survey?.id}
                                </div>
                                <div className="col">
                                    <strong>Survey Unique Id: </strong>
                                    {this.state.users.length > 0 && this.state.users[0].assignUser ?
                                        this.state.users[0].assignUser.temporarySurveyLinkId : this.state.survey?.uniqueid}
                                </div>
                            </div>
                            {this.state.survey?.parentid && isPanelCountryByName(this.state.survey?.country) && (
                                <div className="row mt-2">
                                    <div className="col">
                                        <strong>SMS/WhatsApp Name: </strong>
                                        {this.state.survey?.name}
                                    </div>
                                    <div className="col" />
                                </div>
                            )}

                            {(this.state.survey?.parentid &&
                                <div className="row mt-2">
                                    <div className="col">
                                        <strong>Parent Id: </strong>
                                        {this.state.survey?.parentid}
                                    </div>
                                    <div className="col"></div>
                                </div>
                            )}

                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Project Name: </strong>
                                    {getSurveyDisplayName(this.state.survey)}
                                </div>
                                <div className="col">
                                    <strong>Project Id: </strong>
                                    {this.state.survey?.projectId}
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Start-End IP Validated: </strong>
                                    {setYNinRespo(this.state.survey?.validateStartEndIp === "Y")}
                                </div>
                                <div className="col">
                                    <strong>Country Validated: </strong>
                                    {setYNinRespo(this.state.survey?.validateCountry === "Y")}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                </div>
                                <div className="col">
                                    <strong>Description: </strong>
                                    {this.state.survey?.description}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Start Date: </strong>
                                    {moment(this.state.survey?.createdAt).format('MM/DD/YYYY HH:mm A')}
                                </div>
                                <div className="col">
                                    <strong>End Date: </strong>
                                    {moment(this.state.survey?.expiryDate).format('MM/DD/YYYY HH:mm A')}
                                </div>
                            </div>

                            {_showPanelFields && (<>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Complete Points: </strong>
                                    {this.state.survey?.ceggPoints}
                                </div>
                                <div className="col">
                                    <strong>Over Quota Points: </strong>
                                    {this.state.survey?.overquota}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Quality Terminate Points: </strong>
                                    {this.state.survey?.qualityterminate}
                                </div>
                                <div className="col">
                                    <strong>Terminate Points: </strong>
                                    {this.state.survey?.terminate}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>DX Quota: </strong>
                                    {this.state.survey?.userLimitCutoff}
                                </div>
                                <div className="col">
                                    <strong>Point Allocation Type: </strong>
                                    {this.state.survey?.pointAllocationType}
                                </div>
                            </div>
                            </>)}
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>PM Name: </strong>
                                    {this.state.survey?.pmName}
                                </div>
                                <div className="col">
                                    <strong>Client Quota: </strong>
                                    {this.state.survey?.userLimitCommitted}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Survey Status: </strong>
                                    {showSurveyStatus(this.state.survey?.surveyType)}
                                </div>
                                <div className="col">
                                    <strong>Client: </strong>
                                    {this.state.survey?.client}
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Survey Length: </strong>
                                    {this.state.survey?.surveyLength}
                                </div>
                                <div className="col">
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Speeder Limit (In minutes): </strong>
                                    {this.state.survey?.outlierCutoffTime}
                                </div>
                                <div className="col">
                                    <strong>Cost Per Interview: </strong>
                                    {this.state.survey?.currency ?? ""}
                                    {this.state.survey?.costPerInterview}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Dropouts%: </strong>
                                    {dropoutCalculation(this.state.survey?.dropout_count, this.state.survey?.totalhit_count)}
                                </div>
                                <div className="col">
                                    <strong>Hits: </strong>
                                    {this.state.survey?.totalhit_count ?? 0}
                                </div>
                            </div>

                            <div className="row mt-2">
                                {!_isParent && (
                                <div className="col">
                                    <strong>Use Unique Links: </strong>
                                    {setYNinRespo(this.state.survey?.useUniqueLinks)}
                                </div>
                                )}
                                <div className="col">
                                    <strong>Country: </strong>
                                    {this.state.survey?.country}
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>IP Unique: </strong>
                                    {setYNinRespo(this.state.survey?.ipUnique)}
                                </div>
                                <div className="col">
                                    <strong>Survey URL Identifier: </strong>
                                    {this.state.survey?.surveyUrlIdentifier}
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Client IR: </strong>
                                    {this.state.survey?.clientir ?? "N.A."}
                                </div>
                                {_showPanelFields && (
                                <div className="col">
                                    <strong>Send Survey By: </strong>
                                    {sendSurveyByValue()}
                                </div>
                                )}
                                {!_showPanelFields && <div className="col" />}
                            </div>

                            {!_isParent && (<>
                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Test URL: </strong>
                                    <a href={this.state.survey?.testurl} target="_blank" rel="noopener noreferrer">
                                        {this.state.survey?.testurl}
                                    </a>
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col">
                                    <strong>Live URL: </strong>
                                    <a href={this.state.survey?.url} target="_blank" rel="noopener noreferrer">
                                        {this.state.survey?.url}
                                    </a>
                                </div>
                            </div>
                            </>)}
                            <h6 className='mt-4'>Dynamic Links</h6>
                            <div className="row mt-2">
                                <div className="col">
                                    <div className="alert alert-info" style={{ padding: '8px 12px', fontSize: '0.9em', marginBottom: '8px' }}>
                                        <strong>Note for Survey Provider:</strong> Replace <code>{'{userid}'}</code> in the URLs below with the provider's respondent ID variable (e.g. <code>[%RID%]</code>, <code>{'{{respondent_id}}'}</code>, <code>##RID##</code>, etc.). The <code>{'{userid}'}</code> token is a placeholder — if it arrives at our server unchanged, the hit cannot be registered.
                                    </div>
                                </div>
                            </div>
                            {this.state.clientIdToken ? (
                                <div className="text-muted" style={{ fontSize: '0.9em' }}>
                                    These links already use {this.state.survey?.client}'s respondent ID token <code>{this.state.clientIdToken}</code>.
                                </div>
                            ) : null}
                            {_callbackRows(_dynamicUrl)}
                            <h6 className='mt-4'>Static Links</h6>
                            <div className="row mt-2">
                                <div className="col">
                                    <div className="alert alert-info" style={{ padding: '8px 12px', fontSize: '0.9em', marginBottom: '8px' }}>
                                        <strong>Note for Survey Provider:</strong> Replace <code>{'{userid}'}</code> in the URLs below with the provider's respondent ID variable (e.g. <code>[%RID%]</code>, <code>{'{{respondent_id}}'}</code>, <code>##RID##</code>, etc.). The outcome is recorded from that ID. If <code>{'{userid}'}</code> is left unreplaced, the respondent still sees the thank-you page, but the hit is not registered.
                                    </div>
                                </div>
                            </div>
                            {_callbackRows(_staticUrl)}

                            </>)})()}
                        </Show>

                        {/* Live fieldwork stats — parent + all children, default all time.
                            Its applied filter (dates + Status) also scopes the partner exports below. */}
                        {this.state.surveyIdValue && (
                            <InFieldStats
                                surveyId={this.state.surveyIdValue}
                                parentName={this.state.survey?.projectname || ''}
                                onFilterApplied={(filter) => this.setState({ partnerExportFilter: filter })}
                            />
                        )}

                        {/* Read-only partner stats — add/edit/delete is done via the Partners tab on the Survey edit page */}
                        {this.state.partnerDetails && this.state.partnerDetails.length > 0 && (
                            <div className="jumbotron bg-white p-3 border shadow-sm mt-3">
                                <h6>Partners:</h6>
                                <Table responsive size="sm" bordered>
                                    <thead className="thead-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Partner Name</th>
                                            {!this.state.survey?.parentid && <th>Markets</th>}
                                            <th>Status</th>
                                            <th>Quota</th>
                                            <th>Completed</th>
                                            <th>Terminated</th>
                                            <th>Over Quota</th>
                                            <th>Quality Terminated</th>
                                            <th>PQ Terminated</th>
                                            <th>Verisoul Blocked</th>
                                            <th>Hits</th>
                                            <th>Dropout%</th>
                                            <th>IR%</th>
                                            {this.state.survey?.useUniqueLinks && <th>Links</th>}
                                            <th title="Uses the In-Field Stats filter: From / To dates (IST) and Status. Entered, non-deleted entries only, as in the grid.">
                                                Export
                                                {(this.state.partnerExportFilter?.fromDate || this.state.partnerExportFilter?.toDate) && (
                                                    <small className="d-block text-muted font-weight-normal">
                                                        {this.state.partnerExportFilter.fromDate || '…'} – {this.state.partnerExportFilter.toDate || '…'} IST
                                                    </small>
                                                )}
                                                {this.state.partnerExportFilter?.status && (
                                                    <small className="d-block text-muted font-weight-normal">
                                                        {inFieldStatusLabel(this.state.partnerExportFilter.status)} only
                                                    </small>
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.partnerDetails.map((detail, idx) => {
                                            // Markets badges + drill-down use the backend-computed
                                            // `appliesTo` (shortName[]) — NOT raw selectedMarkets,
                                            // which now holds child IDs (id-based contract). appliesTo
                                            // stays shortName so the badge labels and the
                                            // getPartnerMarketStats drill-down (keyed by s.market =
                                            // child.shortName) work unchanged. 'parent' (string) and
                                            // missing appliesTo fall through to null → "All Markets".
                                            const markets: string[] | null = Array.isArray(detail.appliesTo)
                                                ? detail.appliesTo
                                                : null;
                                            const isParentView = !this.state.survey?.parentid;
                                            // colSpan for the expanded stats row must match the number of <th> in the header.
                                            // Base = #, Partner Name, Status, Quota, Completed, Terminated, Over Quota,
                                            // Quality Terminated, PQ Terminated, Verisoul Blocked, Hits, Dropout%, IR%, Export = 14
                                            // + Markets (parent view only) + Links (useUniqueLinks).
                                            const partnerColCount = 14
                                                + (isParentView ? 1 : 0)
                                                + (this.state.survey?.useUniqueLinks ? 1 : 0);
                                            const expandedStats: any[] | null =
                                                this.state.partnerMarketStatsCache[detail.partnerId] || null;
                                            const expandedMarket = this.state.expandedPartnerMarket?.startsWith(`${detail.partnerId}|`)
                                                ? this.state.expandedPartnerMarket.split('|')[1]
                                                : null;
                                            const showAllMarketsGrid = expandedMarket === '__ALL__' && !!expandedStats;
                                            const expandedRow = expandedMarket && expandedMarket !== '__ALL__' && expandedStats
                                                ? expandedStats.find((s: any) => s.market === expandedMarket)
                                                : null;
                                            const totals = showAllMarketsGrid
                                                ? (expandedStats || []).reduce((acc: any, s: any) => ({
                                                    completes: acc.completes + (s.completes || 0),
                                                    terminates: acc.terminates + (s.terminates || 0),
                                                    overQuota: acc.overQuota + (s.overQuota || 0),
                                                    qualityTerminated: acc.qualityTerminated + (s.qualityTerminated || 0),
                                                    verisoulBlocked: acc.verisoulBlocked + (s.verisoul_terminate_count || 0),
                                                    hasVerisoulEnabled: true,
                                                    hits: acc.hits + (s.hits || 0),
                                                }), { completes: 0, terminates: 0, overQuota: 0, qualityTerminated: 0, verisoulBlocked: 0, hasVerisoulEnabled: true, hits: 0 })
                                                : null;
                                            return (
                                                <React.Fragment key={detail.partnerId}>
                                                <tr>
                                                    <td>{idx + 1}</td>
                                                    <td>{detail.partner_name ?? '-'}</td>
                                                    {isParentView && (
                                                        <td>
                                                            {detail.appliesToData && detail.appliesToData !== 'parent' && detail.appliesToData.length > 0
                                                                ? detail.appliesToData.map((mData: any) => {
                                                                    const m = mData.shortName;
                                                                    const key = `${detail.partnerId}|${m}`;
                                                                    const active = this.state.expandedPartnerMarket === key;
                                                                    const loading = this.state.loadingPartnerMarket === key;
                                                                    const suffix = extractDisplaySuffix(mData.childProjectName, this.state.survey?.projectname || '', m);
                                                                    const display = suffix ? (
                                                                        <React.Fragment>
                                                                            <strong>{m}</strong><span style={{ fontWeight: 'normal', color: '#e0e0e0' }}> - {suffix}</span>
                                                                        </React.Fragment>
                                                                    ) : m;
                                                                    return (
                                                                        <span
                                                                            key={m}
                                                                            className={`badge ${active ? 'badge-primary' : 'badge-info'} mr-1`}
                                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                            title={active ? 'Click to collapse' : `Click to show ${m} stats`}
                                                                            onClick={() => this.togglePartnerMarket(detail.partnerId, m)}
                                                                        >
                                                                            {display}{loading ? ' …' : ''}
                                                                        </span>
                                                                    );
                                                                })
                                                                : markets && markets.length > 0 ? markets.map((m) => {
                                                                    const key = `${detail.partnerId}|${m}`;
                                                                    const active = this.state.expandedPartnerMarket === key;
                                                                    const loading = this.state.loadingPartnerMarket === key;
                                                                    return (
                                                                        <span
                                                                            key={m}
                                                                            className={`badge ${active ? 'badge-primary' : 'badge-info'} mr-1`}
                                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                            title={active ? 'Click to collapse' : `Click to show ${m} stats`}
                                                                            onClick={() => this.togglePartnerMarket(detail.partnerId, m)}
                                                                        >
                                                                            {m}{loading ? ' …' : ''}
                                                                        </span>
                                                                    );
                                                                })
                                                                : (() => {
                                                                    const key = `${detail.partnerId}|__ALL__`;
                                                                    const active = this.state.expandedPartnerMarket === key;
                                                                    const loading = this.state.loadingPartnerMarket === key;
                                                                    return (
                                                                        <span
                                                                            className={`badge ${active ? 'badge-primary' : 'badge-secondary'}`}
                                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                            title={active ? 'Click to collapse' : 'Click to show all markets breakdown'}
                                                                            onClick={() => this.togglePartnerMarket(detail.partnerId, '__ALL__')}
                                                                        >
                                                                            All Markets{loading ? ' …' : ''}
                                                                        </span>
                                                                    );
                                                                })()
                                                            }
                                                        </td>
                                                    )}
                                                    <td>{detail.surveyType ?? '-'}</td>
                                                    <td>{detail.quota ?? '-'}</td>
                                                    <td>{setStyleVal(detail, 'complete', 'completed')}</td>
                                                    <td>{setStyleVal(detail, 'terminate', 'terminated')}</td>
                                                    <td>{setStyleVal(detail, 'over_quota', 'overquota')}</td>
                                                    <td>{setStyleVal(detail, 'quality_terminate', 'qualityTerminated')}</td>
                                                    <td>{detail.pq_terminated_count ?? 0}</td>
                                                    <td>{detail.verisoul_terminate_count ?? 0}</td>
                                                    <td>{detail.hit ?? '0'}</td>
                                                    <td>{dropoutCalculation(detail.dropout, detail.hit)}%</td>
                                                    <td>{detail.ir != null && !isNaN(Number(detail.ir)) ? Number(detail.ir).toFixed(1) + '%' : '-'}</td>
                                                    {this.state.survey?.useUniqueLinks && (
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            {/* Claim/Download interface removed (Unified Pool) */}
                                                        </td>
                                                    )}
                                                    <td className="text-center">
                                                        <button className="btn btn-sm btn-success" onClick={() => this.downloadPartnerUsersFunc(detail.partnerId)}>
                                                            <FontAwesomeIcon icon={['fas', 'download']} />
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isParentView && expandedRow && (
                                                    <tr>
                                                        <td colSpan={partnerColCount} style={{ backgroundColor: '#f8f9fa', padding: '10px 20px' }}>
                                                            <strong>{expandedRow.market}{expandedRow.country && expandedRow.country !== expandedRow.market ? ` — ${expandedRow.country}` : ''}</strong> stats for <em>{detail.partner_name}</em>
                                                            <Table size="sm" bordered className="mt-2 mb-0" style={{ maxWidth: 800 }}>
                                                                <thead className="thead-light">
                                                                    <tr>
                                                                        <th>Completed</th>
                                                                        <th>Terminated</th>
                                                                        <th>Over Quota</th>
                                                                        <th>Quality Term.</th>
                                                                        <th>Verisoul Blocked</th>
                                                                        <th>Hits</th>
                                                                        <th>Dropout%</th>
                                                                        <th>IR%</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        <td>{expandedRow.completes}</td>
                                                                        <td>{expandedRow.terminates}</td>
                                                                        <td>{expandedRow.overQuota}</td>
                                                                        <td>{expandedRow.qualityTerminated}</td>
                                                                        <td>{expandedRow.verisoul_terminate_count ?? 0}</td>
                                                                        <td>{expandedRow.hits}</td>
                                                                        <td>{expandedRow.dropoutPct}%</td>
                                                                        <td>{expandedRow.irPct === '-' ? '-' : `${expandedRow.irPct}%`}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </Table>
                                                        </td>
                                                    </tr>
                                                )}
                                                {isParentView && showAllMarketsGrid && (
                                                    <tr>
                                                        <td colSpan={partnerColCount} style={{ backgroundColor: '#f8f9fa', padding: '10px 20px' }}>
                                                            <strong>All Markets</strong> breakdown for <em>{detail.partner_name}</em>
                                                            <Table size="sm" bordered className="mt-2 mb-0">
                                                                <thead className="thead-light">
                                                                    <tr>
                                                                        <th>Market</th>
                                                                        <th>Completed</th>
                                                                        <th>Terminated</th>
                                                                        <th>Over Quota</th>
                                                                        <th>Quality Term.</th>
                                                                        <th>Verisoul Blocked</th>
                                                                        <th>Hits</th>
                                                                        <th>Dropout%</th>
                                                                        <th>IR%</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(expandedStats || []).length === 0 && (
                                                                        <tr><td colSpan={9} className="text-center text-muted">No data available</td></tr>
                                                                    )}
                                                                    {(expandedStats || []).map((s: any) => (
                                                                        <tr key={s.market}>
                                                                            <td>
                                                                                <span className="badge badge-info mr-1">{s.market}</span>
                                                                                {s.country && s.country !== s.market && s.country !== '-' ? s.country : ''}
                                                                                {(() => {
                                                                                    const suffix = extractDisplaySuffix(s.childProjectName || s.name, this.state.survey?.projectname || '', s.market);
                                                                                    if (suffix) {
                                                                                        return <span className="text-muted ml-1" style={{ fontSize: '0.85em' }}>- {suffix}</span>;
                                                                                    }
                                                                                    return null;
                                                                                })()}
                                                                            </td>
                                                                            <td>{s.completes}</td>
                                                                            <td>{s.terminates}</td>
                                                                            <td>{s.overQuota}</td>
                                                                            <td>{s.qualityTerminated}</td>
                                                                            <td>{s.verisoul_terminate_count ?? 0}</td>
                                                                            <td>{s.hits}</td>
                                                                            <td>{s.dropoutPct}%</td>
                                                                            <td>{s.irPct === '-' ? '-' : `${s.irPct}%`}</td>
                                                                        </tr>
                                                                    ))}
                                                                    {totals && (expandedStats || []).length > 0 && (
                                                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#e9ecef' }}>
                                                                            <td>Total</td>
                                                                            <td>{totals.completes}</td>
                                                                            <td>{totals.terminates}</td>
                                                                            <td>{totals.overQuota}</td>
                                                                            <td>{totals.qualityTerminated}</td>
                                                                            <td>{totals.verisoulBlocked}</td>
                                                                            <td>{totals.hits}</td>
                                                                            <td>{totals.hits > 0 ? (((totals.hits - totals.completes) / totals.hits) * 100).toFixed(2) : '0.00'}%</td>
                                                                            <td>{(totals.completes + totals.terminates) > 0 ? ((totals.completes / (totals.completes + totals.terminates)) * 100).toFixed(2) + '%' : '-'}</td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </Table>
                                                        </td>
                                                    </tr>
                                                )}
                                                {isParentView && expandedMarket && expandedMarket !== '__ALL__' && expandedStats && !expandedRow && (
                                                    <tr>
                                                        <td colSpan={partnerColCount} style={{ backgroundColor: '#f8f9fa', padding: '10px 20px' }} className="text-muted">
                                                            No activity for <strong>{expandedMarket}</strong> yet.
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        <Alert
                            variant="danger"
                            show={this.state.status === PageStatus.Error}
                        >
                            {this.state.error}
                        </Alert>

                        {/* Per-portal panel-country block (Indiapolls / UKpolls / etc).
                            Only rendered when this row's market is one of the panel
                            countries — non-panel markets (US, CA, DE, …) don't operate
                            a portal so the table is meaningless there and is hidden. */}
                        <Show when={this.state.users.length !== 0 && isPanelCountry(this.state.survey?.shortName)} >
                            <div className="jumbotron bg-white p-3 border shadow-sm mt-3">
                                <div className="jumbotron bg-white p-1 mt-2 shadow-sm">
                                    <h6 className='mt-4'>{getDetailsLabel(this.state.survey?.shortName)}:</h6>
                                    <Table responsive size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Completed </th>
                                                <th>Terminated </th>
                                                <th>Over Quota </th>
                                                <th>Quality Terminated</th>
                                                <th>PQ Terminated</th>
                                                <th>Verisoul Blocked</th>
                                                <th>Hits</th>
                                                <th>Dropouts%</th>
                                                <th>IR%</th>
                                                <th>Median LOI</th>
                                                <th>Export User</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                this.state.dxPanelDetails?.map((panelDets, index) => (
                                                    <tr key={"dxPanelDets" + index}>
                                                        <td>{index + 1}</td>
                                                        <td>{panelDets.completed_count ?? "-"}</td>
                                                        <td>{panelDets.terminated_count ?? "-"}</td>
                                                        <td>{panelDets.over_quota_count ?? "-"}</td>
                                                        <td>{panelDets.quality_terminated_count ?? "-"}</td>
                                                        <td>{panelDets.pq_terminated_count ?? 0}</td>
                                                        <td>{panelDets.verisoul_terminate_count ?? 0}</td>
                                                        <td>{panelDets.totalhit_count ?? "-"}</td>
                                                        <td>{dropoutCalculation(panelDets?.dropout_count, panelDets?.totalhit_count)}</td>
                                                        <td>
                                                            {Number(panelDets.completed_count) + Number(panelDets.terminated_count) > 0
                                                                ? ((Number(panelDets.completed_count) / (Number(panelDets.completed_count) + Number(panelDets.terminated_count))) * 100).toFixed(2)
                                                                : '-'
                                                            }
                                                        </td>
                                                        <td>
                                                            {panelDets.finalLOI && !isNaN(Number(panelDets.finalLOI))
                                                                ? Number(panelDets.finalLOI).toFixed(2)
                                                                : "-"
                                                            }
                                                        </td>
                                                        <td className='text-center'>
                                                            {/* Per-portal "Export User" panelist icon — same green button,
                                                                same icon; passes exportType='panelist' so the backend
                                                                filters the UNION to Source='panelist' rows. UI text/icon
                                                                are unchanged on purpose. */}
                                                            <button className="btn btn-sm btn-success" onClick={() => this.handleExport('panelist')}>
                                                                <FontAwesomeIcon
                                                                    icon={['fas', 'download']}
                                                                />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </Table>
                                    <h6 className='text-center'>{this.state.dxPanelDetails.length > 0 ? "" : "No Data Found"}</h6>
                                </div>
                            </div>
                        </Show>

                        {/* Respondents table — always visible when the survey is loaded.
                            In Test mode PMs need to see the generated test IDs even
                            before anyone has taken the survey, so this block is NOT
                            gated on users.length. UsersPaginations renders an empty
                            tbody gracefully; we add a "No respondents yet" fallback. */}
                        <Show when={!!this.state.survey}>
                            {(() => {
                                // Bucket by assignedSurveys.surveyType (stamped at insertion).
                                // Test                 → Test Users (no Link column)
                                // Open / Live / (other non-null, non-Test) → Panelists  (with Link column)
                                // Null/empty (legacy)   → dropped from both buckets.
                                // Why both 'Open' and 'Live': legacy IN dispatch stamps 'Open',
                                // newer dispatch (and reverse-sync for JP/UK/SG/MY/PH) stamps
                                // 'Live' (the survey's current type). A strict `=== 'Open'`
                                // dropped every JP row even though they're real panelists.
                                const allUsers: any[] = this.state.users || [];
                                const testUsers = allUsers.filter((u: any) => u.surveyType === 'Test');
                                const panelists = allUsers.filter((u: any) => u.surveyType && u.surveyType !== 'Test');

                                const renderHeader = (label: string, expanded: boolean, toggle: () => void) => (
                                    <h6
                                        style={{ cursor: 'pointer', userSelect: 'none', marginBottom: expanded ? undefined : 0 }}
                                        onClick={toggle}
                                        aria-expanded={expanded}
                                        role="button"
                                    >
                                        <em className={`fa fa-caret-${expanded ? 'down' : 'right'} mr-2`} />
                                        {label}
                                    </h6>
                                );

                                const renderTable = (
                                    rows: any[],
                                    isPanelistsBucket: boolean,
                                    emptyMessage: string,
                                ) => {
                                    if (rows.length === 0) {
                                        return <h6 className='text-center text-muted mt-2'>{emptyMessage}</h6>;
                                    }
                                    // Panelists bucket carries the original (pre-split) column set:
                                    //   S.No | User Id | Name | Gender | Status | Started At | Link
                                    // Test Users bucket shows: S.No | User Id | Status | Started At | Link
                                    // (Name/Gender are meaningless for anonymous test UUIDs)
                                    return (
                                        <div className="mt-2">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>User Id</th>
                                                        {isPanelistsBucket && <th>Name</th>}
                                                        {isPanelistsBucket && <th>Gender</th>}
                                                        <th>Status</th>
                                                        <th>Started At</th>
                                                        <th>Link</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map((info: any, index: number) => (
                                                        <tr key={(info.userId || 'row') + '-' + index}>
                                                            <td>{index + 1}</td>
                                                            <td>{info.userId || '-'}</td>
                                                            {isPanelistsBucket && (
                                                                <td>{(info.userName && info.userName.trim()) ? info.userName : '-'}</td>
                                                            )}
                                                            {isPanelistsBucket && (
                                                                <td>{info.gender || '-'}</td>
                                                            )}
                                                            <td>{info.status || '-'}</td>
                                                            <td>{moment(info.createdAt).format('MM/DD/YYYY HH:mm A')}</td>
                                                            <td>
                                                                {info.surveylink ? (
                                                                    <a href={info.surveylink} target="_blank" rel="noopener noreferrer">
                                                                        Click here to start survey
                                                                    </a>
                                                                ) : 'NA'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        <div className='jumbotron bg-white p-1 mt-2 shadow-sm'>
                                            <div className="d-flex justify-content-between align-items-center">
                                                {renderHeader(
                                                    'Test Users:',
                                                    this.state.isTestUsersExpanded,
                                                    () => this.setState({ isTestUsersExpanded: !this.state.isTestUsersExpanded }),
                                                )}
                                                {testUsers.length > 0 && (
                                                    <button
                                                        className="btn btn-sm btn-success mr-2"
                                                        onClick={() => {
                                                            const exportData = testUsers.map((u: any, idx: number) => ({
                                                                'S.No': idx + 1,
                                                                'User Id': u.userId || '',
                                                                'Status': u.status || '',
                                                                'Started At': moment(u.createdAt).format('MM/DD/YYYY HH:mm A'),
                                                                'Link': u.surveylink || '',
                                                            }));
                                                            exportToCSV(exportData, 'test_unique_links_export');
                                                        }}
                                                    >
                                                        Export Test Links
                                                    </button>
                                                )}
                                            </div>
                                            {this.state.isTestUsersExpanded && renderTable(testUsers, false, 'No test users yet')}
                                        </div>

                                        {/* Panelists are a panel-country concept only (IN, UK, MY, PH,
                                            JP, SG). Non-panel markets (US, AU, CA, …) and parent-level
                                            views (country is null) have no panelists by definition, so
                                            hide the section entirely there — don't render an empty
                                            state. Accept either country name or shortName because
                                            different survey shapes populate only one of the two. */}
                                        {(isPanelCountryByName(this.state.survey?.country)
                                          || isPanelCountry(this.state.survey?.shortName)) && (
                                            <div className='jumbotron bg-white p-1 mt-2 shadow-sm'>
                                                {renderHeader(
                                                    'Panelists:',
                                                    this.state.isPanelistsExpanded,
                                                    () => this.setState({ isPanelistsExpanded: !this.state.isPanelistsExpanded }),
                                                )}
                                                {this.state.isPanelistsExpanded && renderTable(panelists, true, 'No panelists yet')}
                                            </div>
                                        )}

                                        {totalResponsePages && totalResponseData && (
                                            <div>
                                                <PaginationNumbering
                                                    currentPage={currentPageNumber}
                                                    returnPagenumber={this.handlePageNumberChange}
                                                    showTotalData={totalResponseData}
                                                    sendLastPage={totalResponsePages}
                                                />
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </Show>

                        {/* Email Templates + Email Schedule are panel-only
                            features (IN, UK, SG, MY, PH, JP). Non-panel markets
                            (US, CA, DE, CN, …) do not operate these channels,
                            so both sections stay hidden there. We accept either
                            the full country name (`country`) or the two-letter
                            shortName because different survey shapes populate
                            only one of the two fields. */}
                        {this.state.survey &&
                         (isPanelCountryByName(this.state.survey?.country)
                          || isPanelCountry(this.state.survey?.shortName)) &&
                            <>
                                <DashboardTemplates id={this.state.surveyIdValue} indiaPolls={isIndiaMarket(this.state.survey)} />
                                <SurveyEmailSchedule id={this.state.surveyIdValue} isUnique={this.state.survey?.useUniqueLinks} country={this.state.survey?.shortName} />
                            </>
                        }

                        {/* Audit trail for this survey + its children — see
                            Components/ChangeLog/SurveyChangeLog.tsx */}
                        {this.state.survey && this.state.surveyIdValue &&
                            <SurveyChangeLog surveyId={this.state.surveyIdValue} />
                        }
                    </Modal.Body>
                </Modal>

                <Modal
                    show={this.state.showCloneModal}
                    onHide={() => this.setState({ showCloneModal: false, olderCountries: [] })}
                    backdrop="static"
                    centered
                    size="md"
                    style={{ zIndex: 1201, background: 'rgba(0, 0, 0, 0.5)' }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="contained-modal-title-vcenter">
                            {this.state.cloneTypeValue} Clone
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Show when={this.state.status === PageStatus.Loading}>
                            <div className="d-flex justify-content-center w-100 p-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Show>
                        {/* SMS/Whatsapp Name field is hidden on Group Clone — PMs now set
                            this per-country after cloning. Individual Clone keeps the field
                            because it flows to the single cloned parent's name column. */}
                        {this.state.cloneTypeValue !== 'Group' && (
                            <div className="row mt-2">
                                <div className="col">
                                    <label htmlFor="name">SMS/Whatsapp Name*</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="name"
                                        name="name"
                                        maxLength={30}
                                        value={this.state.name}
                                        onChange={(e) => this.setState({ name: e.target.value })}
                                        placeholder='Max 30 characters'
                                    />
                                    <small className="text-muted">
                                        {(this.state.name || '').length}/30
                                    </small>
                                    <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: '0.85rem' }}>
                                        <strong>WhatsApp preview:</strong>{' '}
                                        Hi {'{{userName}}'}, we have a new survey <strong>{this.state.name || '...'}</strong> for you. Click the link to participate.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="row mt-2">
                            <div className="col">
                                <label htmlFor="projectName">Project Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="projectName"
                                    name="projectName"
                                    value={this.state.projectName}
                                    onChange={(e) => this.setState({ projectName: e.target.value })}
                                    placeholder='Enter here'
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label htmlFor="projectId">Project Id*</label>
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text">DXD-</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="projectId"
                                        name="projectId"
                                        value={(this.state.projectId || '').startsWith('DXD-') ? (this.state.projectId || '').slice(4) : (this.state.projectId || '')}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(/\D/g, '');
                                            if (val.length > 4) val = val.slice(0, 4);
                                            this.setState({ projectId: val ? 'DXD-' + val : '' });
                                        }}
                                        placeholder="1234"
                                        maxLength={4}
                                        pattern="\d{4}"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col">
                                <label htmlFor='countryNewTitle'>Add New Market*</label>
                                <Select
                                    name='countryNewTitle'
                                    id='countryNewTitle'
                                    onChange={this.handleMoreCountryChange}
                                    value={this.state.selectedMoreCountryOption}
                                    required
                                    options={this.state.countries}
                                    isMulti
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => this.cloneThisSurvey()}>
                            Submit
                        </Button>
                        {/* "Submit For Same Market" button removed. Group Clone is now
                            the only clone path for parents and always opens fresh
                            redirect links — the same-market variant (which reused the
                            source survey's redirect URLs) was a footgun. */}
                    </Modal.Footer>
                </Modal>

                <Modal
                    show={this.state.showUploadLinkModal}
                    onHide={() => this.setState({ showUploadLinkModal: false, bulkUploadLinkData: [], selectedPartner4TestLink: '', sendLinksTime: '' })}
                    backdrop="static"
                    centered
                    size="md"
                    style={{ zIndex: 1201, background: 'rgba(0, 0, 0, 0.5)' }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="contained-modal-title-vcenter">
                            Upload File
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Show when={this.state.status === PageStatus.Loading}>
                            <div className="d-flex justify-content-center w-100 p-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Show>
                        <div className="row mt-2">
                            <div className="col">
                                <label htmlFor="uploadfile">File*</label>
                                <input
                                    type="file"
                                    accept='.csv'
                                    className="form-control"
                                    id="uploadfile"
                                    name="uploadfile"
                                    onChange={(e) => this.handleUploadfile(e)}
                                    placeholder='Enter here'
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="info" onClick={() => this.downloadSampleLinks()}>
                            Download Sample
                        </Button>
                        <Button variant="primary" onClick={() => this.uplaodPartnerLinkFunc()}>
                            Submit
                        </Button>
                    </Modal.Footer>
                </Modal>



                <Show when={this.state.showManual}>
                    <ManualSurveyRewards
                        surveyId={this.state.surveyIdValue}
                        show={this.state.showManual}
                        onClose={() => this.setState({ showManual: false, id: '' })}
                    />
                </Show>
                {this.state.survey && this.state.showReconcile && (
                    <ReconciliationUpload
                        surveyId={this.state.survey.id}
                        show={this.state.showReconcile}
                        onClose={() => this.setState({ showReconcile: false })}
                    />
                )}
                {/* Partner config modal removed — use Survey Creation module */}
            </>
        );
    }
}

const DashboardDetailsWithRouter = withRouter(DashboardDetails);

export { DashboardDetailsWithRouter as DashboardDetails };
